import {
  WeeklyDinnerItem,
  WeeklyDinnerItemUpdate,
} from "@mjolnerbacken/shared/types/weekly-dinner-item";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { FETCH_ERROR } from "../consts/fetch-error";
import {
  getWeeklyDinnerItems,
  updateWeeklyDinnerItem,
} from "../services/weekly-dinner";

export function useWeeklyDinnerItems() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["weekly-dinner-items"],
    queryFn: () => getWeeklyDinnerItems(),
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });

  const isInvalidating = useRef(false);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: WeeklyDinnerItemUpdate }) =>
      updateWeeklyDinnerItem(id, data),
    onMutate: ({ id, data }) => {
      // Get old list
      const oldList = queryClient.getQueryData<WeeklyDinnerItem[]>([
        "weekly-dinner-items",
      ]);

      if (!oldList) return { oldList };

      // Update item in list
      const newList = oldList.map((item) =>
        item.id === id ? { ...item, ...data } : item,
      );

      // Set new list
      queryClient.setQueryData(["weekly-dinner-items"], newList);

      // Return context
      return { oldList, newList };
    },
    onError: (error, variables, context) => {
      // Rollback to old list
      queryClient.setQueryData(["weekly-dinner-items"], context?.oldList);
    },
    onSuccess: () => {
      // Invalidate query to get fresh data
      queryClient.invalidateQueries({
        queryKey: ["weekly-dinner-items"],
      });
    },
  });

  return {
    // Query data and states
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isInvalidating: isInvalidating.current,
    error: query.error,
    refetch: query.refetch,

    invalidateQuery: async () => {
      isInvalidating.current = true;
      await queryClient.invalidateQueries({
        queryKey: ["get-shopping-cart-items"],
      });
      isInvalidating.current = false;
    },

    // Update mutation
    updateItem: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  };
}
